// @givmo/disclosure — the disclaimer-registry client types (Combined Plan 03 §7a).
//
// FEDERATED MODEL: the backend DB disclaimer registry is the single runtime source for every
// app-shown legal disclosure; web resolves per-surface copy via `GET /disclaimers/resolve` exactly
// as iOS does — never hardcoding legal copy. This package de-bespokes the donate app's `GIFT_*`
// resolver into one client every surface reuses.
//
// FLOOR POSTURE (iOS parity — `givmo-ios` `DisclaimerFloor.swift` is the reference):
//   - STATICALLY-FLOORED surfaces (GIFT_CARD_PURCHASE, ADD_FUNDS) pass a compiled-in floor whose
//     bytes are byte-identical to the iOS floor and whose hash is pinned + drift-guarded both ends.
//     404 / any error / empty body → the floor renders. The floor is NOT a registry row: it carries
//     no version id and is NEVER echoed on a donation body.
//   - FLOOR-LESS surfaces (DONATE_CONFIRMATION, CAUSE_ETF, …) pass no floor. 404 = "no disclosure
//     right now" (dark-DRAFT) → `null` → the surface renders NOTHING and echoes nothing. Never an
//     error, never a throw — the same ships-dark posture as the iOS floor-less accessors.

/** The disclaimer surface key the backend registry keys on (open string union — tolerant reader). */
export type DisclosureSurface = string;

/**
 * The donation-target key for a target-derived resolve (LCG-GT19): the server rolls the target up
 * to its beneficiary charity set and derives the per-charity AB-488 variant. At most ONE target may
 * ride a resolve (the endpoint 400s on more).
 */
export type DisclosureTarget =
  | { readonly kind: 'fundraiser'; readonly id: string }
  | { readonly kind: 'charity'; readonly id: string }
  | { readonly kind: 'etf'; readonly id: string }
  | { readonly kind: 'collection'; readonly id: string };

/**
 * The funding rail of a rail-keyed resolve (the endpoint's `funding`): `card` for every card-network
 * method (Apple Pay and Google Pay ask for `card`), `wallet` for a Givmo balance. On the one-time
 * `DONATE_CONFIRMATION` / `CAUSE_ETF` resolve it picks the WALLET (grant) body vs the card body
 * (CAM-X3); with `recurring` it picks the recurring body's rail (PV3-BE8).
 */
export type DisclosureFunding = 'card' | 'wallet';

/**
 * Who pays (the endpoint's `payer`, CAM-X3): `company` for a company paying from its own funds,
 * whose body drops the individual-only tax language. Rides with `funding` on the one-time surfaces.
 * Absent = the person's body, exactly as before the parameter existed.
 */
export type DisclosurePayer = 'person' | 'company';

/** A compiled-in floor: the pinned lines + the pinned `content_hash` (byte-identical to iOS). */
export interface DisclosureFloor {
  readonly lines: readonly string[];
  readonly contentHash: string;
}

/** The resolved disclosure a surface renders: registry row, or the compiled-in floor. */
export interface ResolvedDisclosure {
  /** The disclosure lines (registry `content_plaintext` split on `\n`, or the floor lines). */
  readonly lines: readonly string[];
  /** The `content_hash` (registry value, or the pinned floor hash) — the ETag / echo hash. */
  readonly contentHash: string;
  /**
   * The registry document id (`DisclaimerResolveOut.id`) — the `shown_disclaimer_version_id` echo
   * value. `null` for the compiled-in floor (the floor is not a registry row and is never echoed).
   */
  readonly versionId: string | null;
  /** Whether this is the compiled-in floor (true) or a live registry row (false). */
  readonly isFloor: boolean;
  /**
   * The registry row's `applies_to_state` — the variant the server resolved for this request
   * (`A_UNCLAIMED`, `B_VERIFIED_WALLET`, `COMPANY`, …). `null` for the compiled-in floor, which is
   * no registry row. A caller that must show ONE variant checks it and treats any other as a failed
   * resolve: a backend that predates a key such as `payer` ignores it and answers another variant
   * with a 200 (the company deposit, CAM-X4). Optional, so a value built by hand without it (a test
   * double) reads as "not that variant" rather than failing to compile.
   */
  readonly appliesToState?: string | null;
}

/** Materialize a compiled-in floor as the `ResolvedDisclosure` a surface renders. */
export function floorDisclosure(floor: DisclosureFloor): ResolvedDisclosure {
  return {
    lines: floor.lines,
    contentHash: floor.contentHash,
    versionId: null,
    isFloor: true,
    appliesToState: null,
  };
}

/** The wire echo fields a donation-create body carries when a LIVE disclosure was shown. */
export interface DisclosureEchoFields {
  shown_disclaimer_version_id?: string;
  shown_disclaimer_content_hash?: string;
}

/**
 * The donation-time pinning echo (03 §8): the exact wire fields to spread into a donation-create
 * body. Returns the two `shown_disclaimer_*` keys ONLY for a LIVE registry row — a floor (no
 * version id) or an absent disclosure returns `{}` so the wire body stays byte-for-byte legacy
 * (the server stays the authority and pins the resolved version itself).
 */
export function disclosureEcho(
  disclosure: ResolvedDisclosure | null | undefined,
): DisclosureEchoFields {
  if (!disclosure || disclosure.isFloor || disclosure.versionId === null) return {};
  return {
    shown_disclaimer_version_id: disclosure.versionId,
    shown_disclaimer_content_hash: disclosure.contentHash,
  };
}

/**
 * Substitute the literal `{charity_name}` placeholder client-side (03 §7a). A no-op on copy that
 * carries no placeholder. The DONATE_CONFIRMATION registry bodies carry this runtime token; the
 * resolved registry plaintext and its hash remain untouched while consuming surfaces fill only
 * the lines they render.
 */
export function substituteCharityName(
  lines: readonly string[],
  charityName: string,
): readonly string[] {
  return lines.map((line) => line.replaceAll('{charity_name}', charityName));
}

/** LCG-GT26 interim generic fill for INSTANCE-1, the grant target. */
export const GENERIC_CHARITY_NAME_GRANT_TARGET = 'the charities this fundraiser supports';

/** LCG-GT26 interim generic fill for INSTANCE-2, the A_UNCLAIMED non-consent subject. */
export const GENERIC_CHARITY_NAME_UNCLAIMED_SUBJECT = 'Each of them';

/**
 * Fill ambiguous-fundraiser `{charity_name}` tokens for rendering only (LCG-GT26). The anchored
 * A_UNCLAIMED subject MUST be replaced first; the general grant-target fill then replaces every
 * remaining token (including the sole B_VERIFIED instance).
 */
export function substituteCharityNameGeneric(lines: readonly string[]): readonly string[] {
  return lines.map((line) =>
    line
      .replaceAll(
        "{charity_name} hasn't signed up with Givmo",
        `${GENERIC_CHARITY_NAME_UNCLAIMED_SUBJECT} hasn't signed up with Givmo`,
      )
      .replaceAll('{charity_name}', GENERIC_CHARITY_NAME_GRANT_TARGET),
  );
}

/** Split registry `content_plaintext` into block lines on `\n`, dropping empty/blank lines. */
export function splitPlaintext(plaintext: string): string[] {
  return plaintext
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
